

























































































import Vue from 'vue';

import { BAlert, BFormInput, BFormSelect, BFormCheckbox } from 'bootstrap-vue';
import { ajax } from 'jquery';
import { format, parseISO } from 'date-fns';
import { mapKeys, snakeCase, camelCase } from 'lodash';

import DaySelector from '@reciprocity/day-selector';
import Datepicker from '@/components/datepicker/Datepicker.vue';
import GenericCard from '@/components/cards/GenericCard.vue';
import { ModalSize } from '@/components/zen_modal/types';
import { reload } from '@/utils';

import { RepeatSettings, ServerResponse } from './types';

const today = new Date();

export default Vue.extend({
  name: 'RecurrenceModal',
  components: {
    GenericCard,
    BAlert,
    BFormInput,
    BFormSelect,
    BFormCheckbox,
    Datepicker,
    DaySelector,
  },
  props: {
    type: {
      type: String,
      default: '',
      required: true,
    },
    initialConfig: {
      type: Object,
      required: true,
    },
    csrfToken: {
      type: String,
      required: true,
    },
    recApiUrl: {
      type: String,
      required: true,
    },
  },
  /* eslint-disable complexity */
  data() {
    const endType = this.resolveEndsType();
    const defaultConfig: RepeatSettings = {
      interval: 1,
      period: 'WEEKLY',
      startDate: <string>format(today, 'yyyy-MM-dd'),
      endDate: <string | null>null,
      maxOccurrences: <number>0,
      daysOfWeek: <string[]>[],
      daysOfMonth: <string[]>[],
      lastDayOfMonth: <boolean>false,
      dateOfYear: <string | null>null,
    };

    return {
      dayOfMonth: <number>this.getDayOfMonth() || today.getDate(),
      dayOfYear: <number>this.getDayOfYear() || today.getDate(),
      monthOfYear: <number>this.getMonthOfYear() || today.getMonth(),
      periodList: [
        { label: 'week', value: 'WEEKLY' },
        { label: 'month', value: 'MONTHLY' },
        { label: 'year', value: 'YEARLY' },
      ],
      dayNames: ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'],
      monthList: [
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December',
      ],
      endsList: ['on', 'after', 'never'],
      endsType: <string>endType,
      errors: <string[]>[],
      config: <RepeatSettings>Object.assign(defaultConfig, this.initialConfig),
    };
  },
  computed: {
    /* eslint-disable complexity */
    postData() {
      const DATE_FORMAT = 'yyyy-MM-dd';
      const data: RepeatSettings = {
        interval: parseInt(this.config.interval),
        period: this.config.period,
        startDate: format(parseISO(this.config.startDate), DATE_FORMAT) || null,
        endDate: null,
        maxOccurrences: null,
        daysOfWeek: null,
        daysOfMonth: null,
        lastDayOfMonth: false,
        dateOfYear: null,
      };

      if (this.endsType == 'after') {
        data.maxOccurrences = parseInt(this.config.maxOccurrences);
      } else if (this.endsType == 'on' && this.config.endDate) {
        data.endDate = format(parseISO(this.config.endDate), DATE_FORMAT);
      }

      if (this.config.period == 'WEEKLY') {
        data.daysOfWeek = this.config.daysOfWeek;
      }
      if (this.config.period == 'MONTHLY') {
        if (this.config.lastDayOfMonth) {
          data.lastDayOfMonth = true;
        } else if (this.dayOfMonth) {
          data.daysOfMonth = [this.dayOfMonth];
        }
      }
      if (this.config.period == 'YEARLY') {
        // Use leap year 2016
        const dateOfYear = new Date(2016, this.monthOfYear, this.dayOfYear);
        data.dateOfYear = format(dateOfYear, DATE_FORMAT);
      }

      const snakeCaseData = mapKeys(data, (v, k) => snakeCase(k));
      return snakeCaseData;
    },
  },
  watch: {
    period() {
      // Reset values on period change
      this.daysOfWeek = [];
      this.dayOfMonth = today.getDate();
      this.dayOfYear = today.getDate();
      this.monthOfYear = today.getMonth();
      this.config.lastDayOfMonth = false;
    },
    endsType: {
      immediate: true,
      handler(newEndsType) {
        // Reset ends values
        const isOn = newEndsType === 'on';
        const isAfter = newEndsType === 'after';

        if (!isAfter) {
          this.config.maxOccurrences = 1;
        } else if (!isOn) {
          this.config.endDate = '';
        }
      },
    },
  },
  created() {
    this.$emit('updateConfig', { size: ModalSize.Small });
  },
  methods: {
    resolveEndsType() {
      if (this.initialConfig.maxOccurrences) {
        return 'after';
      }
      if (!this.initialConfig.endDate) {
        return 'never';
      }
      return 'on';
    },
    getDayOfMonth() {
      if (this.initialConfig.daysOfMonth) {
        return parseInt(this.initialConfig.daysOfMonth[0]);
      }
      return null;
    },
    getDayOfYear() {
      if (this.initialConfig.dateOfYear) {
        return new Date(this.initialConfig.dateOfYear).getDate();
      }
      return null;
    },
    getMonthOfYear() {
      if (this.initialConfig.dateOfYear) {
        return new Date(this.initialConfig.dateOfYear).getMonth();
      }
      return null;
    },
    daysList(month: number = 0) {
      const days = [];
      // Use leap year 2016 and 0 for day, to get the last day in month
      const maxDays = new Date(2016, month + 1, 0).getDate();

      for (let i = 1; i <= maxDays; i++) {
        days.push(i);
      }

      if (this.dayOfYear > maxDays && this.config.period == 'YEARLY') {
        this.dayOfYear = maxDays;
      }

      return days;
    },
    save() {
      this.errors = [];

      ajax({
        type: 'POST',
        url: this.recApiUrl,
        contentType: 'application/json',
        headers: { 'X-CSRF-Token': this.csrfToken },
        data: JSON.stringify({
          repeat_settings: this.postData,
        }),
        success: (response: ServerResponse) => {
          if (response.errors && response.errors.length > 0) {
            this.errors = response.errors;
          } else if (response.success) {
            this.config = mapKeys(response.schedule, (v, k) => camelCase(k));

            const refreshAfter = GGRC.zenModal.modals.length < 2;
            this.$emit('close');
            if (refreshAfter) {
              this.reload();
            }
          }
        },
        error: () => {
          this.errors = ['Ups, something went wrong.'];
        },
      });
    },
    pluralize(count: string, type: string): string {
      return parseInt(count, 10) > 1 ? type + 's' : type;
    },
    reload,
  },
});
